import { action, CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { AttrIds, Places } from 'src/Constants'
import { MainPresenter } from 'src/main/Main.presenter'
import { AnaliseFormKeys, AnaliseFormScope } from '../form'
import condicaoService from './condicao/condicao.service'
import { CondicaoFormKeys } from './condicao/condicao_form.keys'
import regraService from './regra.service'
import { RegraFormKeys } from './regra_form.keys'
import RegraFormScope from './regra_form.scopes'

const LOG = Logger.get('RegraFormPresenter')

export class RegraFormPresenter extends CubePresenter<MainPresenter, RegraFormScope> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new RegraFormScope())
    }

    // :: Instance
    #regraId?: number
    #analiseId?: number

    // :: Methods

    buscarRegraSelecionada = async () => {
        if (!this.#regraId) return

        const regra = await regraService.fetchRegraInfo(this.#analiseId!, this.#regraId)
        if (regra) {
            this.scope.idSelected = regra.id!
            this.scope.nome = regra.nome
            this.scope.acao = regra.acao
            this.scope.rating1 = regra.rating1
            this.scope.rating2 = regra.rating2
            this.scope.enviarNotificacao = regra.enviarNotificacao
            this.scope.emailsNotificacao = regra.emailsNotificacao
            this.scope.condicoes = regra.condicoes ?? []
        }
    }

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new RegraFormKeys(this.app, intent)

        this.#analiseId = keys.analiseId

        keys.dialogSlot(this.scope)

        LOG.debug(`applyParameters ${keys.regraId}`)
        if (initialization) {
            await this.#initialize()
        }

        if (this.#regraId !== keys.regraId) {
            this.#regraId = keys.regraId

            await this.buscarRegraSelecionada()
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new RegraFormKeys(this.app, intent)
        keys.regraId = this.#regraId
        keys.analiseId = this.#analiseId
        LOG.debug(`publishParameters ${keys.regraId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.handleClose.bind(this)
        this.scope.onSave = this.handleSave.bind(this)

        this.scope.onAddCondicao = this.handleAddCondicao.bind(this)
        this.scope.onEditCondicao = this.handleEditCondicao.bind(this)
        this.scope.onDeleteCondicao = this.handleDeleteCondicao.bind(this)
    }

    @action()
    private async handleAddCondicao() {
        const key = new CondicaoFormKeys(this.app)
        key.regraId = this.#regraId
        key.analiseId = this.#analiseId
        await key.flip()
    }

    @action()
    private async handleEditCondicao() {
        if (this.scope.idCondicaoSelecionada === 0) {
            this.app.notify('error', 'Condição deve ser selecionada')
            return
        }
        const newIntent = this.app.newFlipIntent(Places.condicaoForm)

        if (this.scope.idCondicaoSelecionada < 0) {
            const idSelected = this.scope.idCondicaoSelecionada

            const condicaoEdit = this.scope.condicoes.find((c) => c.id === idSelected)

            if (!condicaoEdit) {
                this.app.notify('error', `Condição com id ${this.scope.idCondicaoSelecionada} não encontrada`)
                return
            }

            newIntent.attributes.set(AttrIds.newNpCondicaoAttr, condicaoEdit)
        }

        const key = new CondicaoFormKeys(this.app, newIntent)

        key.condicaoId = this.scope.idCondicaoSelecionada
        key.regraId = this.#regraId
        key.analiseId = this.#analiseId
        await key.flip()
    }

    @action()
    private async handleDeleteCondicao() {
        const idRemover = this.scope.idCondicaoSelecionada
        if (!confirm('Confirma a exclusão da condição?')) {
            return
        }
        if (this.#regraId && idRemover > 0) {
            const deleted = await condicaoService.deleteCondicao(this.#analiseId!, this.#regraId, idRemover)
            if (!deleted) return
        }

        this.scope.condicoes = this.scope.condicoes.filter((c) => c.id !== idRemover)
        this.app.notify('success', 'Removido com sucesso')
    }

    @action()
    private async handleClose() {
        this.app.scope.dialog = undefined
        const key = new AnaliseFormKeys(this.app)
        await key.flip()
    }

    @action()
    private async handleSave() {
        try {
            const regraSave = {
                nome: this.scope.nome,
                acao: this.scope.acao,
                rating1: this.scope.rating1,
                rating2: this.scope.rating2,
                enviarNotificacao: this.scope.enviarNotificacao,
                emailsNotificacao: this.scope.emailsNotificacao
            }

            const retSalvar =
                this.scope.idSelected > 0
                    ? await regraService.updateRegra(this.#analiseId!, this.scope.idSelected, regraSave)
                    : await regraService.saveRegra(this.#analiseId!, { ...regraSave, condicoes: this.scope.condicoes })

            if (retSalvar) {
                this.app.notify('success', 'Salvo com sucesso')

                this.#regraId = retSalvar.id!

                const analiseFormScope = this.app.getPresenter(Places.analiseForm)?.scope as AnaliseFormScope
                if (analiseFormScope) {
                    analiseFormScope.regras = [
                        ...analiseFormScope.regras.filter((r) => r.id !== this.#regraId),
                        retSalvar
                    ]
                }

                await this.buscarRegraSelecionada()
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }
}

export default RegraFormPresenter
